import {
    Menu,
    Transition,
    MenuItems,
    MenuItem,
    MenuButton,
} from '@headlessui/react';
import React, { Fragment, useEffect, useState } from 'react';
import Icon from 'flow/components/ui-components/Icon';
import { i18next } from 'flow/configure-locale';
import { twMerge } from 'tailwind-merge';
import { isMacOs } from 'flow/utils/os-utils-flow';
import IconButton from 'flow/components/ui-components/IconButton';

interface Props {
    message: { text: string; attachment?: { name: string; file: File } };
    handleSend: () => void;
    handleSmsMessage?: () => void;
    handleEmailMessage?: () => void;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const SendButton = ({
    message,
    handleSend,
    handleSmsMessage,
    handleEmailMessage,
    videoDark = false,
}: Props) => {
    const disabled =
        !message ||
        (message.text.trimEnd().length === 0 && !message.attachment);

    const [click, setClick] = useState<() => void>();
    const [description, setDescription] = useState<string>();

    const handlers = [
        { handler: handleSend, label: 'Chat.Tooltip.Message' },
        { handler: handleSmsMessage, label: 'Chat.Tooltip.SMS' },
        { handler: handleEmailMessage, label: 'Chat.Tooltip.Email' },
    ];

    const activeHandler = handlers.find(({ handler }) => handler);

    useEffect(() => {
        setClick(() => activeHandler?.handler);
        setDescription(
            i18next.t(activeHandler?.label || 'Chat.Tooltip.Message'),
        );
    }, [activeHandler]);

    const showSendOptions =
        [handleSend, handleSmsMessage, handleEmailMessage].filter(Boolean)
            .length > 1;

    return (
        <div className="flex">
            <IconButton
                disabled={disabled}
                className={twMerge(
                    'size-8 text-white',
                    showSendOptions
                        ? 'rounded-s-sm rounded-e-none'
                        : 'rounded-sm',
                    disabled &&
                        (videoDark
                            ? 'hover:darken-0 !bg-transparent !text-gray-500'
                            : 'hover:darken-0 bg-gray-300 text-gray-400'),
                    !disabled &&
                        (videoDark
                            ? 'bg-gray-500 text-gray-200 hover:bg-gray-400 hover:text-white'
                            : 'bg-green hover:darken-10'),
                )}
                onClick={click || (() => {})}
                tooltipLabel={description}
            >
                <Icon iconKey="send_message" />
            </IconButton>

            {showSendOptions && (
                <Menu as="div">
                    {({ open }) => (
                        <div className="relative">
                            <Transition
                                show={open}
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="shadow-base absolute end-0 -top-2 z-30 flex w-[320px] -translate-y-full transform flex-col gap-4 rounded-sm bg-white p-3 text-gray-800">
                                    <MenuItems
                                        as="div"
                                        className="flex max-h-[520px] origin-bottom-right flex-col gap-2 overflow-hidden outline-hidden rtl:origin-top-left"
                                    >
                                        {handleSend && (
                                            <MenuItem>
                                                <button
                                                    className="flex h-9 w-full items-center gap-x-2 rounded-sm px-2 hover:bg-gray-200"
                                                    onClick={handleSend}
                                                >
                                                    <Icon iconKey="chat" />
                                                    <div className="flex w-full items-center justify-between">
                                                        <p>
                                                            {i18next.t(
                                                                'Chat.Tooltip.Message',
                                                            )}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {i18next.t(
                                                                'Chat.Tooltip.Default',
                                                            )}
                                                        </p>
                                                    </div>
                                                </button>
                                            </MenuItem>
                                        )}

                                        {handleSmsMessage && (
                                            <MenuItem>
                                                <button
                                                    className={twMerge(
                                                        'flex h-9 w-full items-center justify-between gap-x-2 rounded-sm px-2',
                                                        message.attachment
                                                            ? 'cursor-default'
                                                            : 'hover:bg-gray-200',
                                                    )}
                                                    onClick={handleSmsMessage}
                                                    disabled={
                                                        !!message.attachment
                                                    }
                                                >
                                                    <Icon
                                                        iconKey="sms"
                                                        className={
                                                            message.attachment &&
                                                            'text-gray-300'
                                                        }
                                                    />
                                                    <div className="flex w-full items-center justify-between">
                                                        <p
                                                            className={
                                                                message.attachment &&
                                                                'text-gray-300'
                                                            }
                                                        >
                                                            {i18next.t(
                                                                'Chat.Tooltip.SMS',
                                                            )}
                                                        </p>
                                                    </div>
                                                </button>
                                            </MenuItem>
                                        )}
                                        {handleEmailMessage && (
                                            <MenuItem>
                                                <button
                                                    className={twMerge(
                                                        'flex h-9 w-full items-center justify-between gap-x-2 rounded-sm px-2',
                                                        message.attachment
                                                            ? 'cursor-default'
                                                            : 'hover:bg-gray-200',
                                                    )}
                                                    onClick={handleEmailMessage}
                                                    disabled={
                                                        !message ||
                                                        !!message.attachment
                                                    }
                                                >
                                                    <Icon
                                                        iconKey="email"
                                                        className={
                                                            message.attachment &&
                                                            'text-gray-300'
                                                        }
                                                    />
                                                    <div className="flex w-full items-center justify-between">
                                                        <p
                                                            className={
                                                                message.attachment &&
                                                                'text-gray-300'
                                                            }
                                                        >
                                                            {i18next.t(
                                                                'Chat.Tooltip.Email',
                                                            )}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {isMacOs()
                                                                ? ' [\u2318 + SHIFT + E]'
                                                                : ' [CTRL + SHIFT + E]'}
                                                        </p>
                                                    </div>
                                                </button>
                                            </MenuItem>
                                        )}
                                    </MenuItems>
                                </div>
                            </Transition>

                            <MenuButton
                                className={twMerge(
                                    'ms-0.5 inline-flex w-6 items-center rounded-sm rounded-s-none text-white',
                                    disabled &&
                                        (videoDark
                                            ? 'cursor-default !bg-transparent !text-gray-500'
                                            : 'cursor-default bg-gray-300 text-gray-400'),
                                    !disabled &&
                                        (videoDark
                                            ? 'bg-gray-500 text-gray-200 hover:bg-gray-400 hover:text-white'
                                            : 'bg-green hover:darken-10'),
                                )}
                                disabled={disabled}
                            >
                                <span className="sr-only">
                                    {i18next.t('Tooltip.Nav.ProfilePicker')}
                                </span>

                                <div className="h-8 py-1">
                                    <Icon iconKey="arrow_down" />
                                </div>
                            </MenuButton>
                        </div>
                    )}
                </Menu>
            )}
        </div>
    );
};

export default SendButton;
